import type { VFXEffectConfig } from '../../VFXEffectRegistry';

const TELEPORT_FLASH: VFXEffectConfig = {
  id: 'teleport_flash',
  name: 'Teleport Flash',
  category: 'burst',
  screenFlash: { color: 0x00FFFF, duration: 300 },
  screenShake: { intensity: 3, duration: 300 },
  primitives: [
    { type: 'particles', particleEffectId: 'sparkle', particleCount: 15 },
    { type: 'ellipse', width: 60, height: 120, color: 0x00FFFF, alpha: 0.5, depth: 95, tween: { alpha: 0, scaleX: 0, duration: 400, ease: 'Quad.easeOut' } },
    { type: 'circle', radius: 20, color: 0xFFFFFF, alpha: 0.8, depth: 97, tween: { alpha: 0, scale: 2, duration: 200, ease: 'Power1' } },
    { type: 'star', points: 4, innerRadius: 3, outerRadius: 8, color: 0x00FFFF, alpha: 0.7, depth: 96, tween: { rotation: Math.PI * 0.25, alpha: 0, duration: 300, ease: 'Power2' }, scatter: { count: 4, radius: 25 } }
  ]
};

const ITEM_PICKUP: VFXEffectConfig = {
  id: 'item_pickup',
  name: 'Item Pickup',
  category: 'burst',
  primitives: [
    { type: 'particles', particleEffectId: 'sparkle', particleCount: 5 },
    { type: 'star', points: 5, innerRadius: 5, outerRadius: 15, color: 0xFFFF00, alpha: 0.8, depth: 96, tween: { alpha: 0, scale: 2, rotation: Math.PI, duration: 500, ease: 'Power2' } },
    { type: 'circle', radius: 10, color: 0xFFFFCC, alpha: 0.6, depth: 95, tween: { alpha: 0, scale: 1.8, duration: 350, ease: 'Power2' } }
  ]
};

// Level-up (lane AP, 2026-10-08): plays on the leveling character for every
// client that can see them — so it carries NO screenFlash/screenShake (one
// player's level-up must not flash or shake anyone's screen) and stays
// character-sized: a golden ring + star pop and a "Level N!" caption that
// starts above the nameplate bars (offsetY 96 from the body centre).
const LEVEL_UP: VFXEffectConfig = {
  id: 'level_up',
  name: 'Level Up',
  category: 'burst',
  floatingText: { text: 'Level {newLevel}!', style: 'info', color: '#ffd700', fontSize: '18px', bounce: true, duration: 2200, offsetY: 96 },
  primitives: [
    { type: 'particles', particleEffectId: 'sparkle', particleCount: 16 },
    { type: 'circle', radius: 30, color: 0xFFD700, alpha: 0.45, depth: 93,
      tween: { alpha: 0, scale: 2, duration: 700, ease: 'Power2' } },
    { type: 'circle', radius: 16, color: 0xFFFF00, alpha: 0.6, depth: 94,
      tween: { alpha: 0, scale: 1.8, duration: 550, ease: 'Power2' } },
    { type: 'star', points: 6, innerRadius: 8, outerRadius: 20, color: 0xFFFFFF, alpha: 0.85, depth: 96,
      tween: { alpha: 0, scale: 1.6, rotation: Math.PI, duration: 650, ease: 'Power2' } },
    { type: 'star', points: 4, innerRadius: 4, outerRadius: 9, color: 0xFFD700, alpha: 0.8, depth: 95,
      tween: { y: -24, alpha: 0, rotation: Math.PI * 0.5, duration: 600, ease: 'Power2' },
      scatter: { count: 6, radius: 32 } }
  ]
};

// Boss/elite finisher burst — the golden celebration burst the finisher used
// to borrow from level_up (and with it a stray "LEVEL UP! {newLevel}" caption
// on every boss kill). CombatFeedbackManager.triggerFinisher owns its flash.
const FINISHER_BURST: VFXEffectConfig = {
  id: 'finisher_burst',
  name: 'Finisher Burst',
  category: 'burst',
  screenShake: { intensity: 8, duration: 400 },
  primitives: [
    { type: 'particles', particleEffectId: 'sparkle', particleCount: 30 },
    { type: 'circle', radius: 50, color: 0xFFD700, alpha: 0.5, depth: 93,
      tween: { alpha: 0, scale: 3, duration: 800, ease: 'Power2' } },
    { type: 'circle', radius: 25, color: 0xFFFF00, alpha: 0.7, depth: 94,
      tween: { alpha: 0, scale: 2.5, duration: 600, ease: 'Power2' } },
    { type: 'star', points: 6, innerRadius: 12, outerRadius: 30, color: 0xFFFFFF, alpha: 0.9, depth: 96,
      tween: { alpha: 0, scale: 2, rotation: Math.PI, duration: 700, ease: 'Power2' } },
    { type: 'star', points: 4, innerRadius: 5, outerRadius: 12, color: 0xFFD700, alpha: 0.8, depth: 95,
      tween: { y: -30, alpha: 0, rotation: Math.PI * 0.5, duration: 600, ease: 'Power2' },
      scatter: { count: 8, radius: 50 } }
  ]
};

const QUEST_COMPLETE: VFXEffectConfig = {
  id: 'quest_complete',
  name: 'Quest Complete',
  category: 'burst',
  screenFlash: { color: 0x00FFFF, duration: 300 },
  floatingText: { text: 'Quest Complete!', style: 'info', fontSize: '28px', duration: 2500, offsetY: 60 },
  primitives: [
    { type: 'particles', particleEffectId: 'sparkle', particleCount: 20 },
    // Shape fallback — cyan celebration burst always visible
    { type: 'circle', radius: 40, color: 0x00FFFF, alpha: 0.5, depth: 93,
      tween: { alpha: 0, scale: 2.5, duration: 600, ease: 'Power2' } },
    { type: 'star', points: 5, innerRadius: 8, outerRadius: 20, color: 0xFFFFFF, alpha: 0.8, depth: 95,
      tween: { alpha: 0, scale: 2, rotation: Math.PI * 0.5, duration: 500, ease: 'Power2' },
      scatter: { count: 6, radius: 35 } }
  ]
};

export const DEFAULT_WORLD_VFX: VFXEffectConfig[] = [
  TELEPORT_FLASH,
  ITEM_PICKUP,
  LEVEL_UP,
  FINISHER_BURST,
  QUEST_COMPLETE,
];
